"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { canAccessNavItem } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationsClient";
import { NAV_ITEMS, type NavItem as NavItemType } from "@/components/ui/navItems";
import { toast } from "react-toastify";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();

  const router = useRouter();
  const pathname = usePathname();

  const openTxCheck = () => {
    setOpen(false);
    router.push("/trust-security?check=1");
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const isActive = (to: string) => {
    if (!to) return false;
    if (to === "/") return pathname === "/";
    return pathname === to || pathname.startsWith(to + "/");
  };

 const NavItem = ({ item }: { item: NavItemType }) => {
  const { to, label, disabled } = item;

  if (!canAccessNavItem(item, user, isAuthenticated)) return null;

  if (disabled) {
    return (
      <button
        onClick={() => {
          const msg = isAuthenticated
            ? "🚫 Premium or admin only."
            : "🔐 Please log in first.";

          if (toast?.info) toast.info(msg);
          else console.info(msg);
        }}
        className="flex items-center w-full min-h-[44px] px-4 text-left text-sm text-textSecondary opacity-50 cursor-not-allowed"
        type="button"
      >
        {label}
      </button>
    );
  }

  const active = isActive(to);

  return (
    <Link
      href={to}
      onClick={() => setOpen(false)}
      className={`flex items-center min-h-[44px] px-4 text-sm rounded-md font-medium transition ${
        active
          ? "bg-primary text-white"
          : "text-textPrimary hover:bg-white/10"
      }`}
    >
      {label}
    </Link>
  );
};

  return (
    <>
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 glass border-b border-border flex items-center justify-between px-4 py-3">
        <button
          onClick={() => setOpen((prev) => !prev)}
          className="inline-flex h-11 w-11 -ml-2 items-center justify-center text-textPrimary text-2xl"
          aria-label="Toggle sidebar"
          type="button"
        >
          {open ? "✕" : "☰"}
        </button>

        <div className="flex items-center gap-2">
          <img
            src="/brand/logo.png"
            className="h-8 w-8 rounded-full"
            alt="ReloadPi"
          />
          <span className="text-lg font-bold text-primary">ReloadPi</span>
        </div>

        <button
          onClick={openTxCheck}
          className="inline-flex items-center min-h-[40px] rounded-md border border-border px-3 text-sm text-textPrimary hover:bg-background"
          type="button"
        >
          Check TX
        </button>

        <div className="flex items-center gap-2">
          {isAuthenticated && <NotificationBell />}
        </div>
      </div>

      <aside
        className={`fixed top-0 left-0 h-[100dvh] w-64 glass border-r border-border z-40
        flex flex-col
        transform transition-transform duration-200 ease-in-out
        ${open ? "translate-x-0" : "-translate-x-full"}
        lg:static lg:translate-x-0 lg:transform-none`}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <img
              src="/brand/logo.png"
              className="h-8 w-8 rounded-full"
              alt="ReloadPi"
            />
            <span className="text-lg font-bold text-primary">ReloadPi</span>
          </div>

          <div className="hidden lg:flex items-center gap-2">
            <button
              onClick={openTxCheck}
              className="inline-flex items-center min-h-[40px] rounded-md border border-border px-3 text-sm text-textPrimary hover:bg-background"
              type="button"
            >
              Check TX
            </button>
            {isAuthenticated && <NotificationBell />}
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-1 overscroll-contain">
          {NAV_ITEMS.map((item) => (
          <NavItem key={item.key} item={item} />
        ))}
        </nav>

        <div className="shrink-0 p-4 border-t border-border text-sm text-textPrimary">
          {!isAuthenticated ? (
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  setOpen(false);
                  router.push("/login");
                }}
                className="w-full rounded-md bg-primary text-white px-3 py-2 text-sm font-medium hover:bg-accent transition"
                type="button"
              >
                Login / Register
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <span className="font-medium">
                {user?.piUsername || user?.username}
              </span>
              <button
                onClick={logout}
                className="inline-flex items-center min-h-[44px] text-textSecondary hover:text-error text-left"
                type="button"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </aside>

      {open && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}
    </>
  );
}